"use client";

import React, { useEffect, useState } from "react";
import Table from "@components/Table";
import Shimmer from "@components/Shimmer";
import Image from "next/image";
import callApi from "@api/api";

import styles from "./PointsTable.module.scss";
import { generateWidgetUrl } from "@/utils/widgetHelper";

interface PointsTableProps {
    seriesKey?: string;
    seriesName?: string;
}

const PointsTable = (props: PointsTableProps) => {
    const { seriesKey } = props;

    const [theme, setTheme] = useState<any>();
    const [teams, setTeams] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const savedTheme = localStorage.getItem("theme");
        if (savedTheme) setTheme(savedTheme);
    }, []);

    const battingColumnStyles = {
        team: { minWidth: "74px" },
        matches: { minWidth: "48px !important" },
        wins: { minWidth: "48px !important" },
        losses: { minWidth: "48px !important" },
        points: { minWidth: "48px !important" },
        nrr: { minWidth: "56px !important" },
    };

    const fetchAndTransformData = async () => {
        try {
            setLoading(true);
            const apiResponse = await callApi({
                url: generateWidgetUrl(
                    "sport_cricket_series_team_points_table",
                    seriesKey,
                ),
                method: "GET",
            });

            if (!apiResponse || apiResponse.isError) {
                throw new Error("API returned an error");
            }

            const result = apiResponse?.data?.result;
            if (!result) throw new Error("Invalid API response structure");

            let transformedTeams: any[] = [];

            if (result?.team_data && result?.team_data?.length > 0) {
                transformedTeams = result?.team_data?.map((team: any) => {
                    const totalMatches =
                        (result?.team_points || [])?.find(
                            (t: any) => t.team_id === team.id,
                        )?.team_total_matches || 0;
                    const wins =
                        (result?.team_points || []).find(
                            (t: any) => t.team_id === team.id,
                        )?.team_wins || 0;

                    const losses =
                        (result?.team_points || []).find(
                            (t: any) => t.team_id === team.id,
                        )?.team_losses || 0;

                    const points =
                        (result?.team_points || []).find(
                            (t: any) => t.team_id === team.id,
                        )?.points || 0;
                    const nrr =
                        (result?.team_points || []).find(
                            (t: any) => t.team_id === team.id,
                        )?.nrr || 0;

                    return {
                        team: team["short_name"] || "Unknown",
                        image_url: team["image_url"] || "",
                        matches: totalMatches.toString(), // Convert to string
                        wins: wins.toString(), // Convert to string
                        losses: losses.toString(),
                        points: points.toString(),
                        nrr: nrr.toFixed(3),
                    };
                });

                const teamsWithMatches = transformedTeams?.filter(
                    (team) => team?.matches > 0,
                );
                const teamsWithoutMatches = transformedTeams?.filter(
                    (team) => team.matches === "0",
                );

                // Sort only teams that have played matches
                teamsWithMatches?.sort((a, b) => {
                    if (b.points !== a.points) {
                        return Number(b.points) - Number(a.points);
                    }
                    if (b.nrr !== a.nrr) {
                        return Number(b.nrr) - Number(a.nrr);
                    }
                    return Number(b.matches) - Number(a.matches);
                });

                // Combine sorted teams with matches first, followed by unsorted teams without matches
                transformedTeams = [
                    ...teamsWithMatches,
                    ...teamsWithoutMatches,
                ];
            } else {
                transformedTeams = (result.team_data || [])?.map(
                    (team: any) => ({
                        team: team.short_name || "-",
                        image_url: team.image_url || "",
                        matches: "-",
                        wins: "-",
                        losses: "-",
                        points: "-",
                        nrr: "-",
                    }),
                );
            }

            const formattedData = [
                {
                    groupName: "",
                    teams: transformedTeams,
                },
            ];

            setTeams(formattedData);
        } catch (err: any) {
            setError(err.message || "Something went wrong");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        if (seriesKey) fetchAndTransformData();
    }, [seriesKey]);

    const columns = [
        { accessor: "team", header: "TEAM" },
        { accessor: "matches", header: "MAT" },
        { accessor: "wins", header: "WINS" },
        { accessor: "losses", header: "LOSSES" },
        { accessor: "points", header: "PTS" },
        { accessor: "nrr", header: "NRR" },
    ];

    return (
        <>
            {loading ? (
                <div className={styles.shimmerContainer} data-theme={theme}>
                    <Shimmer theme={theme} height="400px" />
                </div>
            ) : (
                <div className={styles.tableContainer} data-theme={theme}>
                    <div className={styles.headingText} data-theme={theme}>
                        Team Standings
                    </div>
                    {teams?.map((group, groupIndex) => (
                        <div
                            key={groupIndex}
                            className={styles.tableDiv}
                            data-theme={theme}
                        >
                            <p className={styles.groupName} data-theme={theme}>
                                {group.groupName}
                            </p>
                            <div
                                className={styles.tableWrapper}
                                data-theme={theme}
                            >
                                <Table
                                    columns={columns}
                                    data={group.teams.map((team) => ({
                                        ...team,
                                        team: (
                                            <div className={styles.teamCell}>
                                                <Image
                                                    src={team.image_url}
                                                    alt={team.team}
                                                    className={styles.flagIcon}
                                                    width={24}
                                                    height={24}
                                                />
                                                {team.team}
                                            </div>
                                        ),
                                        nrr: (
                                            <span
                                                className={`${styles.nrrCell} ${
                                                    team.nrr < 0
                                                        ? styles.negativeNrr
                                                        : ""
                                                }`}
                                                data-theme={theme}
                                            >
                                                {parseFloat(team.nrr).toFixed(
                                                    2,
                                                )}
                                            </span>
                                        ),
                                    }))}
                                    tableClassName={styles.pointsTable}
                                    headerCellClassName={styles.headerCell}
                                    tableDataClassName={styles.dataCell}
                                    tableBodyClassName={styles.tableBody}
                                    tableHeadClassName={styles.tableHead}
                                    tableRowClassName={styles.tableRow}
                                    tableHeaderClassName={styles.tableHeader}
                                    columnStyles={
                                        battingColumnStyles as {
                                            [key: string]: React.CSSProperties;
                                        }
                                    }
                                    dataTheme={theme}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </>
    );
};

export default PointsTable;
